import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import {
  ShieldAlert, LayoutDashboard, Building2, Users, CreditCard, Headset,
  Megaphone, ClipboardList, ListChecks, RotateCw, LogOut, Ban, Menu, X, ExternalLink,
  GraduationCap, Globe, FileText, ShieldCheck, Flag, History, Mail, UserPlus, UserCog, Activity, Landmark,
  Scale, ClipboardCheck,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import NoIndex from "./NoIndex";
import { BrandLogo } from "./BrandLogo";
import useAccessibleOverlay from "../hooks/useAccessibleOverlay";

const NAV = [
  { to: "/admin",                    label: "Overview",       icon: LayoutDashboard, exact: true },
  { to: "/admin/businesses",         label: "Businesses",     icon: Building2 },
  { to: "/admin/operations",         label: "Support Operations", icon: Activity },
  { to: "/admin/business-websites",  label: "Business Websites", icon: Globe },
  { to: "/admin/website-requests",   label: "Website Requests", icon: FileText },
  { to: "/admin/users",              label: "Users",          icon: Users },
  { to: "/admin/plans",              label: "Plans",          icon: CreditCard },
  { to: "/admin/corporate-finance",  label: "Corporate accounting", icon: Landmark },
  { to: "/admin/legal",              label: "Legal & Compliance", icon: Scale },
  { to: "/admin/legal-readiness",    label: "Legal Readiness", icon: ClipboardCheck },
  { to: "/admin/client-compliance",  label: "Client compliance", icon: ShieldCheck },
  { to: "/admin/compliance",         label: "Acceptance status", icon: ShieldCheck },
  { to: "/admin/support",            label: "Support tickets (legacy)", icon: Headset },
  { to: "/admin/support-agents",     label: "Support agents", icon: UserCog },
  { to: "/admin/reports",            label: "Reports & abuse", icon: Flag },
  { to: "/admin/audit-logs",         label: "Audit logs",     icon: History },
  { to: "/admin/email-preview",      label: "Email templates", icon: Mail },
  { to: "/admin/banned-emails",      label: "Banned Emails",  icon: Ban },
  { to: "/admin/promoters",          label: "Promoters",      icon: Megaphone },
  { to: "/admin/recruitment",        label: "Sales Recruitment", icon: UserPlus },
  { to: "/admin/outreach-logs",      label: "Outreach Logs",  icon: ClipboardList },
  { to: "/admin/promoter-tasks",     label: "Promoter Tasks", icon: ListChecks },
  { to: "/admin/follow-up",          label: "Lead Follow-Up", icon: RotateCw },
  { to: "/admin/academy",            label: "Guides & Games CMS", icon: GraduationCap },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { panelRef, triggerRef } = useAccessibleOverlay(open, () => setOpen(false));
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      <NoIndex title="Admin · ConfirmPH" />
      {/* TOP HEADER: always visible on every screen size */}
      <header className="sticky top-0 z-40 bg-[#0F1F18] text-white shadow"
                data-testid="admin-topbar">
        <div className="flex items-center justify-between px-3 sm:px-4 h-14">
          <div className="flex items-center gap-2">
            <button data-testid="admin-mobile-menu-toggle"
                    ref={triggerRef}
                    aria-label="Open admin navigation"
                    aria-expanded={open}
                    aria-controls="admin-mobile-navigation"
                    onClick={() => setOpen(true)}
                    className="lg:hidden inline-flex items-center justify-center min-w-11 min-h-11 rounded-lg hover:bg-white/10">
              <Menu className="w-5 h-5" aria-hidden="true" />
            </button>
            <Link to="/admin" className="flex items-center gap-2.5">
              <BrandLogo
                variant="icon"
                className="h-8 w-8 rounded-lg bg-white p-0.5"
                alt="ConfirmPH"
                testId="admin-brand-logo"
              />
              <div className="leading-none">
                <div className="font-display font-semibold text-sm">ConfirmPH</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-emerald-300 mt-1">Super Admin</div>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-3 text-xs text-emerald-100/90">
            <Link to="/" data-testid="admin-view-site"
                   className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10">
              <ExternalLink className="w-3.5 h-3.5" /> View site
            </Link>
            <span data-testid="admin-current-user" className="hidden sm:inline truncate max-w-[200px]">{user?.email}</span>
            <button data-testid="admin-logout-button" onClick={logout}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-white/10">
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      {/* DESKTOP SIDEBAR: fixed on lg+ */}
      <aside className="fixed inset-y-14 left-0 w-60 bg-[#0F1F18] text-white p-3 hidden lg:flex flex-col z-30"
              data-testid="admin-sidebar">
        <NavList onNavigate={() => setOpen(false)} />
      </aside>

      {/* MOBILE DRAWER */}
      {open && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                aria-hidden="true"
                data-overlay-backdrop
                onClick={() => setOpen(false)} data-testid="admin-drawer-backdrop" />
          <aside className="fixed inset-y-0 left-0 w-72 bg-[#0F1F18] text-white p-4 z-50 lg:hidden overflow-y-auto"
                  ref={panelRef}
                  id="admin-mobile-navigation"
                  role="dialog"
                  aria-modal="true"
                  aria-label="Admin navigation"
                  tabIndex={-1}
                  data-testid="admin-mobile-drawer">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-emerald-400" />
                <span className="font-display font-semibold">Super Admin</span>
              </div>
              <button data-testid="admin-drawer-close" onClick={() => setOpen(false)}
                      aria-label="Close admin navigation"
                      className="min-w-11 min-h-11 inline-flex items-center justify-center rounded hover:bg-white/10">
                <X className="w-4 h-4" aria-hidden="true" />
              </button>
            </div>
            <NavList onNavigate={() => setOpen(false)} />
          </aside>
        </>
      )}

      {/* MAIN CONTENT */}
      <main className="lg:ml-60 px-3 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-[1600px]"
            data-testid="admin-main">
        <Outlet />
      </main>
    </div>
  );
}

function NavList({ onNavigate }) {
  return (
    <nav className="flex-1 space-y-0.5">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.exact}
                  onClick={onNavigate}
                  data-testid={`admin-nav-${n.to.replace(/^\//, "").replace(/\//g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition ${
                      isActive ? "bg-emerald-600/20 text-emerald-100" : "text-slate-300 hover:bg-white/5"
                    }`}>
          <n.icon className="w-4 h-4" /> {n.label}
        </NavLink>
      ))}
    </nav>
  );
}
